import React from 'react';
import { Card } from '../components/ui/card';

const MarkdownContent = ({ content }) => {
  // Function to convert markdown-style lists
  const processLists = (text) => {
    // Unordered lists
    text = text.replace(
      /(?:^|\n)((?:\*|\-|\+) .+)/g,
      '\n<ul class="list-disc pl-6 my-2">$1</ul>'
    );
    
    // Ordered lists
    return text.replace(
      /(?:^|\n)(\d+\. .+)/g,
      '\n<ol class="list-decimal pl-6 my-2">$1</ol>'
    );
  };

  // Function to convert markdown-style emphasis
  const processEmphasis = (text) => {
    // Bold
    text = text.replace(
      /\*\*(.+?)\*\*/g,
      '<strong class="font-bold">$1</strong>'
    );
    
    // Italic
    return text.replace(
      /\*(.+?)\*/g,
      '<em class="italic">$1</em>'
    );
  };

  // Function to convert markdown-style blockquotes
  const processBlockquotes = (text) => {
    return text.replace(
      /^>\s+(.+)$/gm,
      '<blockquote class="border-l-4 border-gray-300 pl-4 my-4 italic">$1</blockquote>'
    );
  };

  // Function to convert markdown-style tables
  const processTables = (text) => {
    if (text.includes('|')) {
      const lines = text.split('\n');
      let inTable = false;
      let tableHTML = '<div class="overflow-x-auto my-4"><table class="min-w-full table-auto border-collapse">';
      
      lines.forEach((line) => {
        if (line.includes('|')) {
          if (!inTable) {
            inTable = true;
          }
          
          // Header separator
          if (line.includes('|-')) {
            return;
          }
          
          const cells = line.split('|').filter(cell => cell.trim());
          const isHeader = !inTable || line.includes('|-');
          
          tableHTML += '<tr>';
          cells.forEach((cell) => {
            if (isHeader) {
              tableHTML += `<th class="border px-4 py-2 bg-gray-100 dark:bg-gray-800">${cell.trim()}</th>`;
            } else {
              tableHTML += `<td class="border px-4 py-2">${cell.trim()}</td>`;
            }
          });
          tableHTML += '</tr>';
        } else if (inTable) {
          inTable = false;
          tableHTML += '</table></div>';
        }
      });
      
      if (inTable) {
        tableHTML += '</table></div>';
      }
      
      text = text.replace(/(?:^|\n)(.+\|.+)(?:\n|$)/g, tableHTML);
    }
    return text;
  };

  // Function to process horizontal rules
  const processHorizontalRules = (text) => {
    return text.replace(
      /^---+|^___+|^\*\*\*+$/gm,
      '<hr class="my-4 border-t border-gray-300 dark:border-gray-600" />'
    );
  };

  // Function to convert markdown-style headers
  const processHeaders = (text) => {
    return text.replace(
      /^(#{1,6})\s+(.+)$/gm,
      (_, hashes, content) => {
        const level = hashes.length;
        const className = `heading-${level}`;
        return `<h${level} class="${className}">${content}</h${level}>`;
      }
    );
  };

  // Function to convert markdown-style links
  const processLinks = (text) => {
    return text.replace(
      /\[([^\]]+)\]\(([^)]+)\)/g,
      '<a href="$2" class="text-blue-600 hover:text-blue-800 hover:underline">$1</a>'
    );
  };

  // Function to convert markdown-style code blocks
  const processCodeBlocks = (text) => {
    // First handle multi-line code blocks
    text = text.replace(
      /```(\w+)?\n([\s\S]+?)```/g,
      (_, language, code) => `
        <pre class="bg-gray-100 dark:bg-gray-800 rounded-lg p-4 my-4 overflow-x-auto">
          <code class="language-${language || 'text'}">${code.trim()}</code>
        </pre>
      `
    );

    // Then handle inline code
    return text.replace(
      /`([^`]+)`/g,
      '<code class="bg-gray-100 dark:bg-gray-800 rounded px-1 py-0.5">$1</code>'
    );
  };

  // Process Chinese text
  const processChineseText = (text) => {
    return text.replace(
      /([^<>]+)(?![^<]*>)/g,
      (match) => {
        if (/[\u4e00-\u9fa5]/.test(match)) { // Contains Chinese characters
          return `<span class="chinese-text">${match}</span>`;
        }
        return match;
      }
    );
  };

  // Process paragraphs
  const processParagraphs = (text) => {
    return text.replace(
      /(?:\r\n|\r|\n){2,}/g,
      '</p><p>'
    );
  };

  // Process content
  const processContent = (text) => {
    if (!text) return '';
    let processed = text;
    processed = processParagraphs(processed);
    processed = `<p>${processed}</p>`; // Wrap in paragraph tags
    processed = processHeaders(processed);
    processed = processLinks(processed);
    processed = processCodeBlocks(processed);
    processed = processLists(processed);
    processed = processEmphasis(processed);
    processed = processBlockquotes(processed);
    processed = processTables(processed);
    processed = processHorizontalRules(processed);
    processed = processChineseText(processed);
    return processed;
  };

  // Process iframe content separately
  const parts = content ? content.split(/<iframe.*?<\/iframe>/) : [];
  const iframes = content ? content.match(/<iframe.*?<\/iframe>/g) || [] : [];

  return (
    <div className="markdown-content">
      <style jsx global>{`
        .markdown-content {
          color: var(--text-primary);
          line-height: 1.8;
        }

        .markdown-content p {
          margin: 1em 0;
          white-space: pre-wrap;
          word-break: break-word;
        }

        .markdown-content iframe {
          width: 100%;
          aspect-ratio: 16/9;
          margin: 1em 0;
          border-radius: 0.5rem;
          border: none;
        }

        .markdown-content .chinese-text {
          color: #000000;
          font-size: 1.1em;
        }

        .markdown-content ul,
        .markdown-content ol {
          margin: 1em 0;
          padding-left: 1em;
        }

        .markdown-content li {
          margin: 0.5em 0;
        }

        .markdown-content blockquote {
          margin: 1em 0;
          padding-left: 1em;
          border-left: 4px solid #dfe2e5;
          color: #6a737d;
        }

        .markdown-content table {
          border-collapse: collapse;
          width: 100%;
          margin: 1em 0;
        }

        .markdown-content table th,
        .markdown-content table td {
          border: 1px solid #dfe2e5;
          padding: 6px 13px;
        }

        .markdown-content table tr:nth-child(2n) {
          background-color: #f6f8fa;
        }

        .markdown-content code {
          font-family: SFMono-Regular, Consolas, "Liberation Mono", Menlo, monospace;
          padding: 0.2em 0.4em;
          margin: 0;
          font-size: 85%;
          border-radius: 3px;
        }

        .markdown-content pre {
          padding: 16px;
          overflow: auto;
          font-size: 85%;
          line-height: 1.45;
          border-radius: 3px;
        }

        .markdown-content pre code {
          padding: 0;
          margin: 0;
          font-size: 100%;
          background: transparent;
        }

        .markdown-content hr {
          height: 0.25em;
          padding: 0;
          margin: 24px 0;
          background-color: #e1e4e8;
          border: 0;
        }

        @media (prefers-color-scheme: dark) {
          .markdown-content blockquote {
            border-left-color: #30363d;
            color: #8b949e;
          }

          .markdown-content table th,
          .markdown-content table td {
            border-color: #30363d;
          }

          .markdown-content table tr:nth-child(2n) {
            background-color: #161b22;
          }

          .markdown-content code {
            background-color: rgba(110,118,129,0.4);
          }

          .markdown-content hr {
            background-color: #30363d;
          }
        }
        
        .markdown-content .heading-1 {
          font-size: 2.5em;
          font-weight: 700;
          margin-top: 1.5em;
          margin-bottom: 0.8em;
          padding-bottom: 0.3em;
          border-bottom: 2px solid #eaecef;
          color: #000000;
        }
        
        .markdown-content .heading-2 {
          font-size: 2em;
          font-weight: 700;
          margin-top: 1.5em;
          margin-bottom: 0.8em;
          padding-bottom: 0.3em;
          border-bottom: 2px solid #eaecef;
          color: #000000;
        }
        
        .markdown-content .heading-3 {
          font-size: 1.5em;
          font-weight: 600;
          margin-top: 1.2em;
          margin-bottom: 0.6em;
          color: #000000;
        }

        @media (prefers-color-scheme: dark) {
          .markdown-content .heading-1,
          .markdown-content .heading-2,
          .markdown-content .heading-3 {
            color: #ffffff;
            border-bottom-color: #30363d;
          }
        }

        @media (prefers-color-scheme: dark) {
          .markdown-content {
            color: #c9d1d9;
          }
        }
      `}</style>
      {parts.map((part, index) => (
        <React.Fragment key={index}>
          <div dangerouslySetInnerHTML={{ __html: processContent(part) }} />
          {iframes[index] && (
            <div className="my-4" dangerouslySetInnerHTML={{ __html: iframes[index] }} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

const MarkdownViewer = ({ sections = [] }) => {
  return (
    <div className="space-y-8">
      {sections.filter(section => section).map((section, index) => {
        // Handle different possible section formats
        const content = typeof section === 'string' 
          ? section 
          : section?.content || section?.text || '';
        
        return (
          <Card key={index} className="p-6 bg-white dark:bg-gray-800 shadow-lg">
            <MarkdownContent content={content} />
          </Card>
        );
      })}
    </div>
  );
};

export default MarkdownViewer;